// src/components/MatchDetail.js
import React, { useState, useEffect } from 'react';
import { fetchWithIdToken } from '../utils/fetchWithIdToken';
import { useSeason } from '../contexts/SeasonContext';
import TeamSheet from './TeamSheet';
import CompetitionLink from './CompetitionLink';
import '../styles/MatchDetail.css';

/**
 * MatchDetail component - displays detailed information about a specific match
 * Including team sheets, match statistics, form, and other details
 */
const MatchDetail = ({ matchId, teamName, onBack }) => {
    const { selectedSeason } = useSeason();
    const [matchData, setMatchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadMatchDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch detailed match data from API with season parameter
            const data = await fetchWithIdToken(`/api/insights/match-details/${matchId}?season=${selectedSeason}`);
            setMatchData(data);
        } catch (err) {
            console.error('Error loading match details:', err);
            setError(err.message || 'Failed to load match details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMatchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchId, selectedSeason]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="match-detail">
                <button onClick={onBack} className="back-button">
                    ← Back to Match History
                </button>
                <div className="loading">Loading match details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="match-detail">
                <button onClick={onBack} className="back-button">
                    ← Back to Match History
                </button>
                <div className="error">
                    <h3>Error Loading Match</h3>
                    <p>{error}</p>
                    <button onClick={loadMatchDetails}>Retry</button>
                </div>
            </div>
        );
    }

    if (!matchData) {
        return (
            <div className="match-detail">
                <button onClick={onBack} className="back-button">
                    ← Back to Match History
                </button>
                <div className="no-data">No match data available</div>
            </div>
        );
    }

    const {
        match,
        teamSheet,
        opponentSheet,
        formLeadingUp,
        statistics,
        venue,
        weather
    } = matchData;

    return (
        <div className="match-detail">
            <button onClick={onBack} className="back-button">
                ← Back to Match History
            </button>

            {/* Match Header */}
            <div className="match-header">
                <div className="match-date-info">
                    <div className="match-date">{formatDate(match.date)}</div>
                    <div className="match-time">Kick-off: {formatTime(match.date)}</div>
                    <div className="match-competition">
                        <CompetitionLink competition={match.competition} competitionUrl={match.competitionUrl} />
                    </div>
                </div>

                <div className={`match-score-display result-${match.result?.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="team-name-display">{match.teamName || teamName}</div>
                    <div className="score-display">
                        <span className="score-home">{match.teamScore !== undefined ? match.teamScore : '-'}</span>
                        <span className="score-separator">-</span>
                        <span className="score-away">{match.opponentScore !== undefined ? match.opponentScore : '-'}</span>
                    </div>
                    <div className="team-name-display">{match.opponent}</div>
                </div>

                <div className="match-result-badge">
                    <span className={`result-badge result-${match.result?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {match.result || 'N/A'}
                    </span>
                </div>
            </div>

            {/* Match Details */}
            <div className="match-info-grid">
                <div className="info-card">
                    <div className="info-label">Venue</div>
                    <div className="info-value">{match.venue || venue || 'N/A'}</div>
                </div>
                {statistics?.referee && statistics.referee !== 'N/A' && (
                    <div className="info-card">
                        <div className="info-label">Referee</div>
                        <div className="info-value">{statistics.referee}</div>
                    </div>
                )}
                {statistics?.attendance && statistics.attendance !== 'N/A' && (
                    <div className="info-card">
                        <div className="info-label">Attendance</div>
                        <div className="info-value">{statistics.attendance}</div>
                    </div>
                )}
                {weather && (
                    <>
                        {weather.conditions && (
                            <div className="info-card">
                                <div className="info-label">Conditions</div>
                                <div className="info-value">{weather.conditions}</div>
                            </div>
                        )}
                        {weather.temperature && (
                            <div className="info-card">
                                <div className="info-label">Temperature</div>
                                <div className="info-value">{weather.temperature}°C</div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Match Comment */}
            {statistics?.comment && statistics.comment.trim() !== '' && (
                <div className="match-comment-section">
                    <div className="comment-label">Match Notes</div>
                    <div className="comment-text">{statistics.comment}</div>
                </div>
            )}

            {/* Form Leading Up */}
            {formLeadingUp && formLeadingUp.length > 0 && (
                <div className="form-section">
                    <h3>Form Leading Into Match</h3>
                    <div className="form-display">
                        {formLeadingUp.map((result, idx) => (
                            <div
                                key={idx}
                                className={`form-result result-${result.result?.toLowerCase().replace(/\s+/g, '-')}`}
                                title={`${result.opponent} (${result.teamScore}-${result.opponentScore})`}
                            >
                                {result.result?.charAt(0) || '?'}
                            </div>
                        ))}
                    </div>
                    <div className="form-summary">
                        Last {formLeadingUp.length} matches before this game
                    </div>
                </div>
            )}

            {/* Team Lineups - Text Format */}
            <div className="lineups-section">
                <h3>Team Lineups</h3>
                <div className="lineups-grid">
                    {/* Our Team Lineup */}
                    <div className="lineup-card">
                        <h4>{match.teamName || teamName}</h4>
                        <div className="lineup-list">
                            {teamSheet && teamSheet.length > 0 ? (
                                <>
                                    <div className="starting-lineup">
                                        <div className="lineup-category">Starting XV</div>
                                        {teamSheet
                                            .filter(p => {
                                                const pos = p.position_number || parseInt(p.position) || 0;
                                                return pos >= 1 && pos <= 15;
                                            })
                                            .sort((a, b) => {
                                                const posA = a.position_number || parseInt(a.position) || 0;
                                                const posB = b.position_number || parseInt(b.position) || 0;
                                                return posA - posB;
                                            })
                                            .map((player, idx) => {
                                                const pos = player.position_number || parseInt(player.position) || 0;
                                                return (
                                                    <div key={idx} className="lineup-player">
                                                        <span className="player-pos">{pos}</span>
                                                        <span className="player-nm">{player.name}</span>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                    {teamSheet.filter(p => {
                                        const pos = p.position_number || parseInt(p.position) || 0;
                                        return pos >= 16;
                                    }).length > 0 && (
                                            <div className="substitutes-lineup">
                                                <div className="lineup-category">Substitutes</div>
                                                {teamSheet
                                                    .filter(p => {
                                                        const pos = p.position_number || parseInt(p.position) || 0;
                                                        return pos >= 16;
                                                    })
                                                    .sort((a, b) => {
                                                        const posA = a.position_number || parseInt(a.position) || 16;
                                                        const posB = b.position_number || parseInt(b.position) || 16;
                                                        return posA - posB;
                                                    })
                                                    .map((player, idx) => {
                                                        const pos = player.position_number || parseInt(player.position) || 16;
                                                        return (
                                                            <div key={idx} className="lineup-player">
                                                                <span className="player-pos">{pos}</span>
                                                                <span className="player-nm">{player.name}</span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                </>
                            ) : (
                                <div className="no-lineup">No team sheet available</div>
                            )}
                        </div>
                    </div>

                    {/* Opponent Lineup */}
                    <div className="lineup-card">
                        <h4>{match.opponent}</h4>
                        <div className="lineup-list">
                            {opponentSheet && opponentSheet.length > 0 ? (
                                <>
                                    <div className="starting-lineup">
                                        <div className="lineup-category">Starting XV</div>
                                        {opponentSheet
                                            .filter(p => {
                                                const pos = p.position_number || parseInt(p.position) || 0;
                                                return pos >= 1 && pos <= 15;
                                            })
                                            .sort((a, b) => {
                                                const posA = a.position_number || parseInt(a.position) || 0;
                                                const posB = b.position_number || parseInt(b.position) || 0;
                                                return posA - posB;
                                            })
                                            .map((player, idx) => {
                                                const pos = player.position_number || parseInt(player.position) || 0;
                                                return (
                                                    <div key={idx} className="lineup-player">
                                                        <span className="player-pos">{pos}</span>
                                                        <span className="player-nm">{player.name}</span>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                    {opponentSheet.filter(p => {
                                        const pos = p.position_number || parseInt(p.position) || 0;
                                        return pos >= 16;
                                    }).length > 0 && (
                                            <div className="substitutes-lineup">
                                                <div className="lineup-category">Substitutes</div>
                                                {opponentSheet
                                                    .filter(p => {
                                                        const pos = p.position_number || parseInt(p.position) || 0;
                                                        return pos >= 16;
                                                    })
                                                    .sort((a, b) => {
                                                        const posA = a.position_number || parseInt(a.position) || 16;
                                                        const posB = b.position_number || parseInt(b.position) || 16;
                                                        return posA - posB;
                                                    })
                                                    .map((player, idx) => {
                                                        const pos = player.position_number || parseInt(player.position) || 16;
                                                        return (
                                                            <div key={idx} className="lineup-player">
                                                                <span className="player-pos">{pos}</span>
                                                                <span className="player-nm">{player.name}</span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                </>
                            ) : (
                                <div className="no-lineup">No team sheet available</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Sheets - Visual Formation */}
            <div className="teamsheets-section">
                <h3>Team Sheets</h3>
                <div className="teamsheets-grid">
                    <div className="teamsheet-container">
                        <TeamSheet
                            players={teamSheet}
                            teamName={match.teamName || teamName}
                        />
                    </div>
                    <div className="teamsheet-container">
                        <TeamSheet
                            players={opponentSheet}
                            teamName={match.opponent}
                        />
                    </div>
                </div>
            </div>

            {/* Additional Match Notes */}
            {match.notes && (
                <div className="notes-section">
                    <h3>Match Notes</h3>
                    <div className="match-notes">
                        {match.notes}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MatchDetail;
